import { useEffect, useMemo, useRef } from "react";
import { LngLatBounds, Map as MLMap, NavigationControl, Popup } from "maplibre-gl";
import "maplibre-gl/dist/maplibre-gl.css";
import { buildOsmStyle, MAP_CONTAINER_BACKGROUND } from "../lib/mapStyle";
import { parseTimestamp, relativeTime } from "../lib/format";
import { MESHCORE_SNR_COLOR_HEX, MESHCORE_SOURCE_LABEL } from '../lib/definitions/protocols';
import { hasUsablePosition } from "../lib/meshview";
import type { NodeSighting, NodeNeighbor, ObservedDirectLink, MeshCoreLinkEvidence, EstimatedPosition } from "../api/types";

// Node detail's own map (2026-09-23 layout rework; extended 2026-09-24 to
// also draw direct neighbours — see the "Neighbours" block below). Kept
// the original name/file: the position-history track is still the bulk of
// what this draws and the reason the component exists at all, and the
// neighbours added here are additive context on the SAME map rather than
// a second concern — a rename (e.g. NodeMap.tsx) was considered and
// deliberately not done, to avoid an unrelated import churn across
// NodeDetailPage.tsx for a component whose job (render this one node's
// map) hasn't actually changed. Reuses the exact same tile source/theme
// handling as the main LiveMap (lib/mapStyle.ts) rather than
// reimplementing tile config — this is deliberately a much smaller,
// self-contained map, built fresh whenever the point set, neighbour set,
// or theme changes (a full rebuild is cheap at this scale).

// Oldest -> newest color ramp (Malla's green-recent/red-old model, this
// build's own palette): a plain 2-stop RGB lerp keyed by each point's
// position in the chronological sequence (ageRatio), not by absolute time,
// so it reads consistently whether the track spans an hour or a month.
const OLD_COLOR = { r: 0xc9, g: 0x3a, b: 0x2e }; // red — oldest point in the track
const NEW_COLOR = { r: 0x0c, g: 0xa3, b: 0x0c }; // green — most recent point (matches the reception badge color)

interface TrackPoint {
  lat: number;
  lon: number;
  ts: number;
  isRoster: boolean;
}

// A stationary router (never moved) still produces one position report per
// sighting — every one of them the same coordinate, give or take GPS/
// antenna jitter. Fitting map bounds to that near-zero-area box zooms in
// absurdly far on featureless terrain and reads as broken, not "hasn't
// moved" (2026-09-23, reported live against %21d369c0b1 — Hayden Peak
// Router v2). 0.0005deg (~55m at these latitudes) is a tolerance for that
// jitter, not a "nearby" threshold — a node that has genuinely relocated
// even a couple of blocks will exceed it and get the normal fit-bounds
// treatment below. The same helper (and epsilon) is reused for the map's
// own bounds decision below, applied to the node + its neighbours
// together, not just the track — see `allBoundsPoints`.
const STATIONARY_EPSILON_DEG = 0.0005;

/** True when every point in the track sits within STATIONARY_EPSILON_DEG of every other — i.e. this node has effectively never moved. A single point is trivially stationary. */
export function isStationaryTrack(points: { lat: number; lon: number }[]): boolean {
  if (points.length <= 1) return true;
  let minLat = points[0].lat;
  let maxLat = points[0].lat;
  let minLon = points[0].lon;
  let maxLon = points[0].lon;
  for (const p of points) {
    if (p.lat < minLat) minLat = p.lat;
    if (p.lat > maxLat) maxLat = p.lat;
    if (p.lon < minLon) minLon = p.lon;
    if (p.lon > maxLon) maxLon = p.lon;
  }
  return maxLat - minLat < STATIONARY_EPSILON_DEG && maxLon - minLon < STATIONARY_EPSILON_DEG;
}

// Fixed zoom chosen for a stationary single-location track: close enough to
// read as a real place (streets/terrain visible) rather than a dot lost in
// a wide, empty fitBounds view, without being so close it looks like the
// map is trying (and failing) to show movement that isn't there.
const STATIONARY_ZOOM = 14;

// --- Neighbours (2026-09-24, GET /api/nodes/{protocol}/{native_id}/neighbors) ---
// Three evidence tiers, weakest to strongest, per the endpoint's own
// NodeNeighbor shape (api/schemas.py):
//   - "bidirectional": both this node's own NeighborInfo AND the
//     neighbour's own NeighborInfo claim the link. Strongest — drawn
//     solid, thick, saturated.
//   - "one-way": only ONE side's NeighborInfo claims it. Weaker — could
//     be stale, or the path could be genuinely asymmetric (this radio
//     hears them fine but they never hear it back, or vice versa). Drawn
//     thinner and fainter, with a line-gradient that fades toward
//     whichever end did NOT make the claim, so the direction is visible
//     on the line itself, not just in a hover popup.
//   - "observed-only": no NeighborInfo evidence in either direction, just
//     a meshview_direct_links overheard reception. Weakest of the three —
//     not even a claim from either node itself. Drawn dotted and muted.
// A neighbour can have BOTH a one-way NeighborInfo claim AND
// observed_direct_links evidence at once (seen live against
// !00000002's own neighbour !00000003) — that still renders as "one-way"
// (the stronger of its two evidence kinds), with the observed-link
// corroboration mentioned in its hover popup rather than silently merged
// away.
export type NeighborTier = "bidirectional" | "one-way" | "observed-only";

// Exported so NodeDetailPage's own neighbour summary/positionless-list
// (outside this component, below the map) classifies each neighbour
// exactly the same way the map does — one rule, not two copies that could
// drift apart.
export function neighborTier(n: NodeNeighbor): NeighborTier {
  if (n.bidirectional) return "bidirectional";
  if (n.reported_by_node || n.reports_this_node) return "one-way";
  return "observed-only";
}

const BIDIRECTIONAL_COLOR = "#2a78d6"; // blue — matches lib/definitions.ts's CATEGORICAL.blue; strongest evidence
const ONE_WAY_COLOR = "#c98500"; // amber — matches CATEGORICAL.yellow's dark-mode value; one-sided claim
const OBSERVED_ONLY_COLOR = "#898781"; // ink-muted grey; weakest — no NeighborInfo claim either way
// 2026-09-26 operator-review fix #3: was BIDIRECTIONAL_COLOR (same blue as
// the bidirectional-neighbour spokes/markers drawn on top of it) at a
// faint 0.55 outline opacity — the estimate circle all but disappeared
// among a dense cluster of same-colored dots (screenshot review,
// RidgeWatcher). A dedicated, unused-elsewhere hue plus a bolder dashed
// outline makes it visually distinct in both themes; fill stays low-
// opacity and the circle is still added first/underneath everything else
// (see below) so it never obscures a marker.
const ESTIMATE_CIRCLE_COLOR = "#9b3fd6"; // purple — not used by any neighbour tier or track color

// Worst-case colour across every meshcore_direct_links entry for one
// neighbour (red > yellow > green > none) — a neighbour can carry more
// than one evidence row (different source/resolution), and the map draws
// ONE line per neighbour, so this picks the most cautious single verdict
// to show rather than averaging colours together (a red + a green
// blended would read as a false all-clear).
function worstMeshcoreColor(links: MeshCoreLinkEvidence[]): "red" | "yellow" | "green" | null {
  let worst: "red" | "yellow" | "green" | null = null;
  for (const l of links) {
    if (l.snr_color === "red") return "red";
    if (l.snr_color === "yellow") worst = "yellow";
    else if (l.snr_color === "green" && worst === null) worst = "green";
  }
  return worst;
}

function hexToRgbTriplet(hex: string): string {
  const h = hex.replace("#", "");
  const r = parseInt(h.slice(0, 2), 16);
  const g = parseInt(h.slice(2, 4), 16);
  const b = parseInt(h.slice(4, 6), 16);
  return `${r}, ${g}, ${b}`;
}

function rgba(hex: string, alpha: number): string {
  return `rgba(${hexToRgbTriplet(hex)}, ${alpha})`;
}

// 2026-09-26 board item ("Estimated position for nodes without one") —
// a real-world-radius circle (not a fixed on-screen pixel radius, which
// would misrepresent scale as the user zooms) around an estimated
// position, built as a 64-point polygon via the standard destination-
// point formula on a spherical earth (same haversine radius the backend
// used to derive the estimate's own radius_km). No terrain/projection
// correction — a rough circle is honest enough for a number this wide
// (tens of km) at the small map scales this renders at.
const EARTH_RADIUS_KM = 6371.0088;

function circlePolygon(lat: number, lon: number, radiusKm: number, points = 64): number[][] {
  const coords: number[][] = [];
  const latRad = (lat * Math.PI) / 180;
  const lonRad = (lon * Math.PI) / 180;
  const angularDistance = radiusKm / EARTH_RADIUS_KM;
  for (let i = 0; i <= points; i++) {
    const bearing = (i / points) * 2 * Math.PI;
    const destLat = Math.asin(
      Math.sin(latRad) * Math.cos(angularDistance) + Math.cos(latRad) * Math.sin(angularDistance) * Math.cos(bearing),
    );
    const destLon =
      lonRad +
      Math.atan2(
        Math.sin(bearing) * Math.sin(angularDistance) * Math.cos(latRad),
        Math.cos(angularDistance) - Math.sin(latRad) * Math.sin(destLat),
      );
    coords.push([(destLon * 180) / Math.PI, (destLat * 180) / Math.PI]);
  }
  return coords;
}

function escapeHtml(s: string): string {
  return s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");
}

function formatSnr(v: number | null | undefined): string | null {
  return v == null ? null : `${v} dB`;
}

function tierLabel(tier: NeighborTier): string {
  if (tier === "bidirectional") return "Bidirectional";
  if (tier === "one-way") return "One-way";
  return "Observed only";
}

// CSS class suffix per tier — kept distinct from the NeighborTier string
// values themselves ("one-way"/"observed-only") since the existing
// .neighbor-popup-tier-* rules (already themed, added with this map's
// first pass) use the shorter "oneway"/"observed" spelling.
function tierClass(tier: NeighborTier): string {
  if (tier === "bidirectional") return "bidirectional";
  if (tier === "one-way") return "oneway";
  return "observed";
}

function neighborDisplayName(n: NodeNeighbor): string {
  return n.long_name || n.short_name || n.native_id;
}

// One labelled row per observed_direct_links entry — meshview_direct_links
// rows are inherently one-directional (transmitter_is_this_node says which
// way), and the same pair can have a row in each direction, so this
// summarizes all of them rather than assuming there's only ever one.
function observedLinkRows(links: ObservedDirectLink[], nodeName: string, neighborName: string): string {
  return links
    .map((l) => {
      const from = l.transmitter_is_this_node ? nodeName : neighborName;
      const to = l.transmitter_is_this_node ? neighborName : nodeName;
      const snr = formatSnr(l.best_snr);
      const value = `${l.observation_count} reception${l.observation_count === 1 ? "" : "s"}${snr ? `, best ${snr}` : ""} · ${relativeTime(
        l.last_seen_at,
      )}`;
      return popupRow(`${escapeHtml(from)} heard by ${escapeHtml(to)}`, escapeHtml(value));
    })
    .join("");
}

// One labelled row per meshcore_direct_links entry — at most one per
// direction now (2026-09-26 review follow-up merged every underlying
// source/resolution combination into one row server-side; the per-
// resolution breakdown survives as `evidence_summary` text instead of
// separate rows). Shows the mean SNR (the stated default, not
// best — see MeshCoreLinkEvidence's own docstring), the evidence
// source(s), and that breakdown, plainly labelled.
function meshcoreLinkRows(links: MeshCoreLinkEvidence[], nodeName: string, neighborName: string): string {
  return links
    .map((l) => {
      const from = l.transmitter_is_this_node ? nodeName : neighborName;
      const to = l.transmitter_is_this_node ? neighborName : nodeName;
      const snr = formatSnr(l.snr_mean ?? l.snr_latest);
      const sources = l.sources.map((s) => MESHCORE_SOURCE_LABEL[s]).join(" + ");
      const value = `${l.observation_count} reception${l.observation_count === 1 ? "" : "s"}${
        snr ? `, avg ${snr}` : ""
      } · ${sources} · ${l.evidence_summary} · ${relativeTime(l.last_seen_at)}`;
      return popupRow(`${escapeHtml(from)} heard by ${escapeHtml(to)}`, escapeHtml(value));
    })
    .join("");
}

function popupRow(label: string, valueHtml: string): string {
  return `<div class="neighbor-popup-row"><span class="neighbor-popup-label">${label}</span><span class="neighbor-popup-value">${valueHtml}</span></div>`;
}

// Light hover content — just enough to identify the marker (name, short
// name, link type). Full per-direction SNR/last-seen detail lives in the
// click popup below, where there's room; a hover readout that tries to
// say everything is what produced the old run-on-sentence tooltip.
function neighborHoverHtml(n: NodeNeighbor, tier: NeighborTier): string {
  const neighborName = neighborDisplayName(n);
  const parts: string[] = [`<div class="neighbor-popup-name">${escapeHtml(neighborName)}</div>`];
  if (n.short_name && n.short_name !== neighborName) {
    parts.push(`<div class="neighbor-popup-shortname">${escapeHtml(n.short_name)}</div>`);
  }
  parts.push(`<div class="neighbor-popup-tier neighbor-popup-tier-${tierClass(tier)}">${tierLabel(tier)}</div>`);
  return `<div class="neighbor-popup neighbor-popup-hover">${parts.join("")}</div>`;
}

// Full click-popup content — structured, labelled rows rather than prose,
// so the direction/SNR/recency read at a glance instead of requiring the
// old sentence to be parsed. The neighbour's name is the only link in
// here (a plain <a href="#/nodes/..."> — lib/router.ts's useRoute listens
// for "hashchange", so a native anchor click navigates with no extra JS);
// clicking the marker that opens this popup must NOT itself navigate.
function neighborClickHtml(n: NodeNeighbor, tier: NeighborTier, nodeName: string): string {
  const neighborName = neighborDisplayName(n);
  const href = `#/nodes/${encodeURIComponent(n.native_id)}`;
  const parts: string[] = [`<div class="neighbor-popup-name"><a href="${href}">${escapeHtml(neighborName)}</a></div>`];

  if (n.short_name) parts.push(popupRow("Short name", escapeHtml(n.short_name)));
  parts.push(popupRow("Link type", `<span class="neighbor-popup-tier neighbor-popup-tier-${tierClass(tier)}">${tierLabel(tier)}</span>`));

  const outSnr = formatSnr(n.reported_by_node_snr);
  if (n.reported_by_node) {
    const value = `${outSnr ?? "heard"}${n.reported_by_node_at ? ` · ${relativeTime(n.reported_by_node_at)}` : ""}`;
    parts.push(popupRow(`${escapeHtml(nodeName)} → them`, escapeHtml(value)));
  }
  const inSnr = formatSnr(n.reports_this_node_snr);
  if (n.reports_this_node) {
    const value = `${inSnr ?? "heard"}${n.reports_this_node_at ? ` · ${relativeTime(n.reports_this_node_at)}` : ""}`;
    parts.push(popupRow(`Them → ${escapeHtml(nodeName)}`, escapeHtml(value)));
  }

  if (n.observed_direct_links.length > 0) {
    parts.push(`<div class="neighbor-popup-subhead">Direct observation</div>`);
    parts.push(observedLinkRows(n.observed_direct_links, nodeName, neighborName));
  }

  if (n.meshcore_direct_links.length > 0) {
    parts.push(`<div class="neighbor-popup-subhead">MeshCore evidence</div>`);
    parts.push(meshcoreLinkRows(n.meshcore_direct_links, nodeName, neighborName));
  }

  return `<div class="neighbor-popup neighbor-popup-click">${parts.join("")}</div>`;
}

interface NeighborPoint {
  n: NodeNeighbor;
  lat: number;
  lon: number;
  tier: NeighborTier;
}

export function PositionHistoryMap({
  points,
  neighbors,
  centerLat,
  centerLon,
  estimatedPosition,
  nodeName,
  theme,
}: {
  points: NodeSighting[];
  // Undefined/omitted = no neighbour layer at all (e.g. neighbours haven't
  // loaded yet). An empty array is a real "checked, this node has none"
  // answer and renders no neighbour features, same as undefined visually
  // — NodeDetailPage is responsible for the surrounding "0 neighbours"
  // vs. "still loading" text, not this component.
  neighbors?: NodeNeighbor[];
  // This node's own current position, independent of the position-history
  // track above (a node can have a known current position with a track
  // that's empty or stale) — used as the origin for every neighbour line
  // and the map's own "home" marker. Falls back to the track's most
  // recent point when omitted.
  centerLat?: number | null;
  centerLon?: number | null;
  // 2026-09-26 board item ("Estimated position for nodes without one") —
  // ONLY ever set when `centerLat`/`centerLon` above are both null (the
  // backend never computes one otherwise, see EstimatedPosition's own
  // docstring). Drawn as a shaded, translucent circle of radius_km — no
  // pin, never styled like the real "home" marker below — with
  // neighbour spokes drawn FROM it (the estimate IS the best guess of
  // where those neighbours were heard from), never treated as a real
  // reported position anywhere else on this map.
  estimatedPosition?: EstimatedPosition | null;
  nodeName?: string;
  theme: "light" | "dark";
}) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<MLMap | null>(null);

  // Chronological, oldest-first. The history endpoint's own contract says
  // "time-ordered" but not which direction, and the age ramp below depends
  // on knowing which end is newest — sort explicitly rather than trust it.
  const track = useMemo<TrackPoint[]>(() => {
    return points
      .filter((p) => p.lat != null && p.lon != null)
      .map((p) => ({
        lat: p.lat as number,
        lon: p.lon as number,
        ts: parseTimestamp(p.ts ?? p.observed_at) ?? 0,
        isRoster: p.sighting_kind === "roster_snapshot",
      }))
      .sort((a, b) => a.ts - b.ts);
  }, [points]);

  // 2026-09-26 fix: was `n.lat != null && n.lon != null` only — missed the
  // (0, 0) "no GPS fix" sentinel (lib/meshview.ts's own hasUsablePosition,
  // the same rule NodeDetailPage's positioned/unplaced neighbour split
  // already used), so a neighbour reported at exactly (0, 0) rendered as
  // if genuinely positioned: a real marker + spoke line clear across the
  // map to the Gulf of Guinea, and (worse, once the estimated-position
  // circle started drawing spokes from a non-null origin even for a node
  // with no real `center` of its own) dragged the whole view out to a
  // world-scale fitBounds instead of the actual regional cluster. Same
  // rule, one place, now shared with every other (0,0) check in this
  // codebase rather than a second, looser one.
  const positionedNeighbors = useMemo<NeighborPoint[]>(() => {
    return (neighbors ?? [])
      .filter((n) => hasUsablePosition({ lat: n.lat ?? null, lon: n.lon ?? null }))
      .map((n) => ({ n, lat: n.lat as number, lon: n.lon as number, tier: neighborTier(n) }));
  }, [neighbors]);

  // This node's own REAL position: the caller's centerLat/centerLon when
  // given, else the track's most recent point. Every neighbour spoke and
  // the "home" marker are drawn from here ONLY — both would be inventing
  // a position this node was never actually seen at, so both stay gated
  // on `center` (not `mapCenter` below) even after the 2026-09-26 fix.
  const center = useMemo(() => {
    if (centerLat != null && centerLon != null) return { lat: centerLat, lon: centerLon };
    if (track.length > 0) return { lat: track[track.length - 1].lat, lon: track[track.length - 1].lon };
    return null;
  }, [centerLat, centerLon, track]);

  // 2026-09-26 fix: a node with NO known position of its own (this node's
  // own `center` above is null) but one or more POSITIONED neighbours
  // (real case: a MeshCore companion, own position never reported, but
  // 45 of its neighbours have one) used to render nothing but an empty
  // grey box — the effect below bailed out entirely for lack of a center
  // to build the map around. `mapCenter` is ONLY used to pick where the
  // map view starts/fits when there's no real `center` — the neighbour
  // centroid is never drawn as a marker or spoke origin (that would
  // falsely claim to be this node's own position); see the `center`-gated
  // guards on the home marker below, which are unchanged.
  //
  // 2026-09-26 board item ("Estimated position for nodes without one"):
  // when an estimate exists, prefer it over the plain neighbour centroid
  // for view-centring (it IS a considered estimate, weighted by signal —
  // a strictly better guess than an unweighted average of the same
  // neighbours) AND, unlike the plain centroid, it's real enough to also
  // draw the neighbour spokes FROM (`spokeOrigin` below) and a shaded
  // circle around — still never the real "home" marker, never `center`.
  const mapCenter = useMemo(() => {
    if (center) return center;
    if (estimatedPosition) return { lat: estimatedPosition.lat, lon: estimatedPosition.lon };
    if (positionedNeighbors.length === 0) return null;
    const lat = positionedNeighbors.reduce((sum, p) => sum + p.lat, 0) / positionedNeighbors.length;
    const lon = positionedNeighbors.reduce((sum, p) => sum + p.lon, 0) / positionedNeighbors.length;
    return { lat, lon };
  }, [center, estimatedPosition, positionedNeighbors]);

  // Spokes/neighbour markers are drawn from the node's REAL position when
  // known, else from the estimate (still a considered, signal-weighted
  // guess of roughly where this node is, unlike the plain view-centring
  // centroid above) — but NEVER get a "home" pin, and the estimate is
  // never used as `center` itself (which also feeds allBoundsPoints/the
  // home marker) so it can never be mistaken for a reported position.
  const spokeOrigin = center ?? (estimatedPosition ? { lat: estimatedPosition.lat, lon: estimatedPosition.lon } : null);

  const resolvedNodeName = nodeName || "This node";

  // A stable signature so the effect only rebuilds the map when the actual
  // point/neighbour set (or theme) changes, not on every parent re-render.
  const signature = useMemo(() => {
    const trackSig = track.map((p) => `${p.ts}:${p.lat.toFixed(5)}:${p.lon.toFixed(5)}:${p.isRoster ? 1 : 0}`).join(",");
    const neighborSig = positionedNeighbors
      .map((p) => `${p.n.native_id}:${p.lat.toFixed(5)}:${p.lon.toFixed(5)}:${p.tier}:${p.n.reported_by_node ? 1 : 0}:${p.n.reports_this_node ? 1 : 0}`)
      .join(",");
    const centerSig = mapCenter ? `${mapCenter.lat.toFixed(5)}:${mapCenter.lon.toFixed(5)}` : "none";
    const estimateSig = estimatedPosition
      ? `${estimatedPosition.lat.toFixed(5)}:${estimatedPosition.lon.toFixed(5)}:${estimatedPosition.radius_km.toFixed(1)}:${estimatedPosition.neighbour_count}`
      : "none";
    return `${theme}|${centerSig}|${estimateSig}|${trackSig}|${neighborSig}`;
  }, [theme, track, positionedNeighbors, mapCenter, estimatedPosition]);

  // The bounds decision (fixed stationary zoom vs. fit-to-bounds) now
  // considers the node's own position plus every positioned neighbour, not
  // just the track — a stationary router with neighbours 50+ miles away
  // must still zoom out far enough to show them (2026-09-24; the track-only
  // version of this map zoomed to a single point and left neighbours
  // entirely off-screen). Also includes the estimate circle's own extent
  // (2026-09-26) so the shaded circle is never clipped off-screen.
  const allBoundsPoints = useMemo(() => {
    const pts: { lat: number; lon: number }[] = track.map((p) => ({ lat: p.lat, lon: p.lon }));
    for (const p of positionedNeighbors) pts.push({ lat: p.lat, lon: p.lon });
    if (center) pts.push(center);
    if (estimatedPosition) {
      for (const [lon, lat] of circlePolygon(estimatedPosition.lat, estimatedPosition.lon, estimatedPosition.radius_km, 16)) {
        pts.push({ lat, lon });
      }
    }
    return pts;
  }, [track, positionedNeighbors, center, estimatedPosition]);

  const stationary = useMemo(() => isStationaryTrack(allBoundsPoints), [allBoundsPoints]);

  useEffect(() => {
    if (!containerRef.current) return;
    if (track.length === 0 && positionedNeighbors.length === 0 && !estimatedPosition) return;
    // Fix (2026-09-26, screenshot pass): `center` is already null exactly
    // when centerLat/centerLon were never given AND track is empty -- a
    // node with positioned neighbours but no known position of its own
    // (real case hit live: a MeshCore companion node, own position never
    // reported, 45 of its neighbours have one). The old fallback here
    // re-read track[track.length - 1] in that same situation, which is
    // empty, throwing "Cannot read properties of undefined (reading
    // 'lat')" and crashing the whole node page.
    //
    // 2026-09-26 follow-up fix: bailing out entirely here left that exact
    // case (own position unknown, neighbours positioned) rendering an
    // empty grey box with nothing on it — `mapCenter` (see its own
    // useMemo above) now falls back to the neighbour centroid so this map
    // still draws them, centred on their own bounds; the home marker and
    // spokes stay gated on the REAL `center` below (never drawn from an
    // invented position).
    if (!mapCenter) return;
    const initialCenter = mapCenter;
    const map = new MLMap({
      container: containerRef.current,
      style: buildOsmStyle(theme),
      center: [initialCenter.lon, initialCenter.lat],
      zoom: stationary ? STATIONARY_ZOOM : 11,
      attributionControl: { compact: true },
      canvasContextAttributes: { preserveDrawingBuffer: true },
    });
    mapRef.current = map;
    map.addControl(new NavigationControl({ showCompass: false }), "top-right");

    // hoverPopup: light, transient, one per marker — cleared on
    // mouseleave. clickPopup: persistent full-detail popup opened by a
    // click; stays until its own close button, a click elsewhere on the
    // map (maplibre's built-in closeOnClick), or Escape (no maplibre
    // built-in for that — handled explicitly below).
    let hoverPopup: Popup | null = null;
    let clickPopup: Popup | null = null;
    let clickPopupNativeId: string | null = null;

    function onEscape(ev: KeyboardEvent) {
      if (ev.key === "Escape" && clickPopup) clickPopup.remove();
    }
    document.addEventListener("keydown", onEscape);

    map.on("load", () => {
      const n = track.length;

      if (track.length > 0) {
        map.addSource("position-history-line", {
          type: "geojson",
          data: {
            type: "Feature",
            properties: {},
            geometry: { type: "LineString", coordinates: track.map((p) => [p.lon, p.lat]) },
          },
        });
        map.addLayer({
          id: "position-history-line",
          type: "line",
          source: "position-history-line",
          paint: {
            "line-color": theme === "dark" ? "#8a8a86" : "#a8a69c",
            "line-width": 2,
            "line-opacity": 0.8,
          },
        });
      }

      // --- Estimated-position circle (2026-09-26 board item), drawn
      // first/underneath everything else — a shaded, translucent,
      // real-world-radius circle, deliberately never a pin, never styled
      // like the "home" marker below (which only ever draws from a REAL
      // reported position). ---
      if (estimatedPosition) {
        map.addSource("estimated-position-circle", {
          type: "geojson",
          data: {
            type: "Feature",
            properties: {},
            geometry: {
              type: "Polygon",
              coordinates: [circlePolygon(estimatedPosition.lat, estimatedPosition.lon, estimatedPosition.radius_km)],
            },
          },
        });
        map.addLayer({
          id: "estimated-position-circle-fill",
          type: "fill",
          source: "estimated-position-circle",
          paint: { "fill-color": ESTIMATE_CIRCLE_COLOR, "fill-opacity": 0.08 },
        });
        map.addLayer({
          id: "estimated-position-circle-outline",
          type: "line",
          source: "estimated-position-circle",
          paint: { "line-color": ESTIMATE_CIRCLE_COLOR, "line-width": 2.5, "line-opacity": 0.9, "line-dasharray": [3, 2] },
        });
      }

      // --- Neighbour spokes, drawn under the markers/track points added
      // below so a marker always sits visually on top of its own line. ---
      if (spokeOrigin && positionedNeighbors.length > 0) {
        const bidirectional = positionedNeighbors.filter((p) => p.tier === "bidirectional");
        const oneWay = positionedNeighbors.filter((p) => p.tier === "one-way");
        const observedOnly = positionedNeighbors.filter((p) => p.tier === "observed-only");

        const spokeLine = (p: NeighborPoint) => {
          const meshcoreColor = p.n.meshcore_direct_links.length > 0 ? worstMeshcoreColor(p.n.meshcore_direct_links) : null;
          return {
            type: "Feature" as const,
            properties: {
              dir: p.n.reported_by_node && !p.n.reports_this_node ? "out" : "in",
              // Only set for a MeshCore neighbour (empty/undefined for a
              // meshtastic one) — the observed-only layer's paint below
              // falls back to OBSERVED_ONLY_COLOR when this is absent.
              ...(meshcoreColor ? { color: MESHCORE_SNR_COLOR_HEX[meshcoreColor] } : {}),
            },
            geometry: { type: "LineString" as const, coordinates: [[spokeOrigin.lon, spokeOrigin.lat], [p.lon, p.lat]] },
          };
        };

        if (bidirectional.length > 0) {
          map.addSource("neighbor-lines-bidirectional", {
            type: "geojson",
            data: { type: "FeatureCollection", features: bidirectional.map((p) => spokeLine(p)) },
          });
          map.addLayer({
            id: "neighbor-lines-bidirectional",
            type: "line",
            source: "neighbor-lines-bidirectional",
            paint: { "line-color": BIDIRECTIONAL_COLOR, "line-width": 3, "line-opacity": 0.85 },
          });
        }

        if (oneWay.length > 0) {
          // A single lineMetrics source shared by two layers (one per
          // claim direction) — line-gradient is a per-LAYER paint
          // property keyed on ["line-progress"] (0 at each feature's
          // first vertex, 1 at its last), so the two layers mirror the
          // same stops to make the CLAIMING end read as the brighter one
          // regardless of which end that is. Every feature's coordinates
          // are ordered [thisNode, neighbour] uniformly; only the
          // gradient direction differs per layer/filter.
          map.addSource("neighbor-lines-oneway", {
            type: "geojson",
            lineMetrics: true,
            data: { type: "FeatureCollection", features: oneWay.map((p) => spokeLine(p)) },
          });
          map.addLayer({
            id: "neighbor-lines-oneway-out",
            type: "line",
            source: "neighbor-lines-oneway",
            filter: ["==", ["get", "dir"], "out"],
            paint: {
              "line-width": 2,
              "line-gradient": ["interpolate", ["linear"], ["line-progress"], 0, rgba(ONE_WAY_COLOR, 0.85), 1, rgba(ONE_WAY_COLOR, 0.12)],
            },
          });
          map.addLayer({
            id: "neighbor-lines-oneway-in",
            type: "line",
            source: "neighbor-lines-oneway",
            filter: ["==", ["get", "dir"], "in"],
            paint: {
              "line-width": 2,
              "line-gradient": ["interpolate", ["linear"], ["line-progress"], 0, rgba(ONE_WAY_COLOR, 0.12), 1, rgba(ONE_WAY_COLOR, 0.85)],
            },
          });
        }

        if (observedOnly.length > 0) {
          map.addSource("neighbor-lines-observed", {
            type: "geojson",
            data: { type: "FeatureCollection", features: observedOnly.map((p) => spokeLine(p)) },
          });
          map.addLayer({
            id: "neighbor-lines-observed",
            type: "line",
            source: "neighbor-lines-observed",
            paint: {
              // A MeshCore neighbour carries its own per-feature `color`
              // (the SNR policy, worstMeshcoreColor above); a
              // meshview-observed-only (meshtastic) neighbour has none and
              // falls back to the flat grey this layer always used.
              "line-color": ["coalesce", ["get", "color"], OBSERVED_ONLY_COLOR],
              "line-width": ["case", ["has", "color"], 2.5, 1.5],
              "line-opacity": ["case", ["has", "color"], 0.85, 0.6],
              "line-dasharray": [1, 2],
            },
          });
        }
      }

      if (track.length > 0) {
        map.addSource("position-history-points", {
          type: "geojson",
          data: {
            type: "FeatureCollection",
            features: track.map((p, i) => ({
              type: "Feature" as const,
              properties: {
                // 1 = most recent point, 0 = oldest — see the color ramp above.
                ageRatio: n > 1 ? i / (n - 1) : 1,
                isRoster: p.isRoster ? 1 : 0,
              },
              geometry: { type: "Point" as const, coordinates: [p.lon, p.lat] },
            })),
          },
        });
        map.addLayer({
          id: "position-history-points",
          type: "circle",
          source: "position-history-points",
          paint: {
            "circle-radius": 6,
            "circle-color": [
              "interpolate",
              ["linear"],
              ["get", "ageRatio"],
              0,
              `rgb(${OLD_COLOR.r}, ${OLD_COLOR.g}, ${OLD_COLOR.b})`,
              1,
              `rgb(${NEW_COLOR.r}, ${NEW_COLOR.g}, ${NEW_COLOR.b})`,
            ],
            // Reception vs roster-snapshot stays visible on the map, not just
            // in the list below it: a roster-snapshot point (present in our
            // radio's own node database as of a poll, not a live reception —
            // see NodeDetailPage's SIGHTING_KIND_* comment, migration 0011)
            // renders fainter with a heavier outline rather than a solid
            // dot, so it doesn't read as an equally-certain on-air hearing.
            "circle-opacity": ["case", ["==", ["get", "isRoster"], 1], 0.5, 0.95],
            "circle-stroke-width": ["case", ["==", ["get", "isRoster"], 1], 2, 1],
            "circle-stroke-color": theme === "dark" ? "#ffffff" : "#0b0b0b",
          },
        });
      }

      // --- Neighbour markers — one shared layer, styled per tier via
      // match expressions, so click/hover only needs to bind once. ---
      if (positionedNeighbors.length > 0) {
        map.addSource("neighbor-points", {
          type: "geojson",
          data: {
            type: "FeatureCollection",
            features: positionedNeighbors.map((p) => {
              const meshcoreColor = p.n.meshcore_direct_links.length > 0 ? worstMeshcoreColor(p.n.meshcore_direct_links) : null;
              return {
                type: "Feature" as const,
                properties: {
                  nativeId: p.n.native_id,
                  tier: p.tier,
                  ...(meshcoreColor ? { color: MESHCORE_SNR_COLOR_HEX[meshcoreColor] } : {}),
                },
                geometry: { type: "Point" as const, coordinates: [p.lon, p.lat] },
              };
            }),
          },
        });
        map.addLayer({
          id: "neighbor-points",
          type: "circle",
          source: "neighbor-points",
          paint: {
            "circle-radius": ["match", ["get", "tier"], "bidirectional", 7, "one-way", 6, 5],
            "circle-color": [
              "coalesce",
              ["get", "color"],
              ["match", ["get", "tier"], "bidirectional", BIDIRECTIONAL_COLOR, "one-way", ONE_WAY_COLOR, OBSERVED_ONLY_COLOR],
            ],
            "circle-opacity": ["match", ["get", "tier"], "bidirectional", 0.95, "one-way", 0.75, 0.55],
            "circle-stroke-width": ["match", ["get", "tier"], "bidirectional", 2.5, "one-way", 1.5, 1],
            "circle-stroke-color": theme === "dark" ? "#ffffff" : "#0b0b0b",
          },
        });

        map.on("mouseenter", "neighbor-points", (e) => {
          map.getCanvas().style.cursor = "pointer";
          const f = e.features?.[0];
          if (!f) return;
          const nativeId = f.properties?.nativeId as string;
          // Already showing the full click popup for this exact marker —
          // don't stack a second, lighter popup on top of it.
          if (nativeId === clickPopupNativeId) return;
          const np = positionedNeighbors.find((p) => p.n.native_id === nativeId);
          if (!np) return;
          hoverPopup?.remove();
          hoverPopup = new Popup({ closeButton: false, closeOnClick: false, offset: 14, maxWidth: "200px" })
            .setLngLat([np.lon, np.lat])
            .setHTML(neighborHoverHtml(np.n, np.tier))
            .addTo(map);
        });
        map.on("mouseleave", "neighbor-points", () => {
          map.getCanvas().style.cursor = "";
          hoverPopup?.remove();
          hoverPopup = null;
        });
        // Click opens (or replaces) a PERSISTENT popup — it must not
        // navigate by itself; only the neighbour's name link inside the
        // popup (built by neighborClickHtml) does that. Left click-to-
        // navigate behind: "when i click on a node on the map it
        // shouldn't take me there immediately" — reported defect.
        map.on("click", "neighbor-points", (e) => {
          const f = e.features?.[0];
          if (!f) return;
          const nativeId = f.properties?.nativeId as string;
          const np = positionedNeighbors.find((p) => p.n.native_id === nativeId);
          if (!np) return;
          hoverPopup?.remove();
          hoverPopup = null;
          clickPopup?.remove();
          clickPopup = new Popup({ closeButton: true, closeOnClick: true, offset: 14, maxWidth: "280px" })
            .setLngLat([np.lon, np.lat])
            .setHTML(neighborClickHtml(np.n, np.tier, resolvedNodeName))
            .addTo(map);
          clickPopupNativeId = nativeId;
          clickPopup.on("close", () => {
            clickPopup = null;
            clickPopupNativeId = null;
          });
        });
      }

      // --- "Home" marker: this node's own position, drawn last so it's
      // never buried under a neighbour marker sitting nearby. ---
      if (center) {
        map.addSource("home-node-point", {
          type: "geojson",
          data: { type: "Feature", properties: {}, geometry: { type: "Point", coordinates: [center.lon, center.lat] } },
        });
        map.addLayer({
          id: "home-node-point",
          type: "circle",
          source: "home-node-point",
          paint: {
            "circle-radius": 8,
            "circle-color": theme === "dark" ? "#ffffff" : "#0b0b0b",
            "circle-stroke-width": 3,
            "circle-stroke-color": theme === "dark" ? "#0b0b0b" : "#ffffff",
          },
        });
      }

      // A stationary track/neighbourhood already got its fixed
      // STATIONARY_ZOOM center above — fitBounds on a near-zero-area box
      // would just zoom in far past that, past any use, on empty terrain.
      // Otherwise fit to the node + every positioned neighbour + the full
      // track, so neighbours well outside the track's own footprint are
      // never left off-screen.
      if (allBoundsPoints.length > 1 && !stationary) {
        let bounds = new LngLatBounds([allBoundsPoints[0].lon, allBoundsPoints[0].lat], [allBoundsPoints[0].lon, allBoundsPoints[0].lat]);
        for (const p of allBoundsPoints) bounds.extend([p.lon, p.lat]);
        map.fitBounds(bounds, { padding: 48, maxZoom: 15, duration: 0 });
      }
    });

    return () => {
      document.removeEventListener("keydown", onEscape);
      hoverPopup?.remove();
      clickPopup?.remove();
      map.remove();
      mapRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signature]);

  if (track.length === 0 && positionedNeighbors.length === 0 && !estimatedPosition) return null;

  return (
    <div
      ref={containerRef}
      className="position-history-map"
      style={{ background: MAP_CONTAINER_BACKGROUND[theme] }}
    />
  );
}
